import { useCallback, useEffect, useState } from "react";
import classNames from "classnames";
import {
  $getRoot,
  $getSelection,
  EditorState,
  $createTextNode,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  TextFormatType,
  ElementFormatType,
  FORMAT_ELEMENT_COMMAND,
  COMMAND_PRIORITY_LOW,
} from "lexical";
import {
  $isListNode,
  INSERT_CHECK_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListNode,
  REMOVE_LIST_COMMAND,
} from "@lexical/list";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { HeadingNode, $createHeadingNode } from "@lexical/rich-text";
import { mergeRegister } from "@lexical/utils";
import {
  BoldIcon,
  ToolBoxDivider,
  ItalicIcon,
  ImageIcon,
  AttachmentIcon,
  AlignmentIcon,
  DownAlignmentArrowIcon,
  NumberListIcon,
  BulletPointIcon,
  LeftAlignIcon,
  RightAlignIcon,
  UnderLineIcon,
} from "components/atoms/Icon";
import { Button } from "components/atoms";
import "./index.scss";

const theme = {
  ltr: "ltr",
  rtl: "rtl",
  list: {
    nested: {
      listitem: "editor-nested-listitem",
    },
    ol: "editor-list-ol",
    ul: "editor-list-ul",
    listitem: "editor-listItem",
    listitemChecked: "editor-listItemChecked",
    listitemUnchecked: "editor-listItemUnchecked",
  },
  hashtag: "editor-hashtag",
  image: "editor-image",
  link: "editor-link",
  text: {
    bold: "editor-textBold",
    italic: "editor-textItalic",
    underline: "editor-textUnderline",
  },
};

const onError = (error: any) => {
  console.error(error);
};

const Toolbar = () => {
  const [editor] = useLexicalComposerContext();

  const [isShowSub, setIsShowSub] = useState(false);

  const handleTextOperation = (formatType: TextFormatType) => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, formatType);
  };

  const handleElementOperation = (formatType: ElementFormatType) => {
    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, formatType);
  };

  const formatBulletList = () => {
    editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
  };

  return (
    <div className="tool-bar-wrapper">
      <div className="tool-bar-container">
        <ToolBoxDivider />
        <div
          className={classNames("bold")}
          onClick={() => {
            handleTextOperation("bold");
          }}
        >
          <BoldIcon />
        </div>
        <div
          className={classNames("italic")}
          onClick={() => {
            handleTextOperation("italic");
          }}
        >
          <ItalicIcon />
        </div>

        <div
          className={classNames("underline")}
          onClick={() => {
            handleTextOperation("underline");
          }}
        >
          <UnderLineIcon />
        </div>
        <ToolBoxDivider />
        <div className={classNames("image")}>
          <ImageIcon />
        </div>
        <div className={classNames("attachment")}>
          <AttachmentIcon />
        </div>
        <ToolBoxDivider />
        <div
          className={classNames("alignment")}
          onClick={() => setIsShowSub((prev) => !prev)}
        >
          <AlignmentIcon />
          <DownAlignmentArrowIcon />
          {isShowSub && (
            <div className="sub">
              <div
                onClick={() => {
                  handleElementOperation("left");
                }}
              >
                Left
              </div>
              <div
                onClick={() => {
                  handleElementOperation("right");
                }}
              >
                Right
              </div>
              <div
                onClick={() => {
                  handleElementOperation("center");
                }}
              >
                Center
              </div>
              <div
                onClick={() => {
                  handleElementOperation("justify");
                }}
              >
                Justify
              </div>
            </div>
          )}
        </div>
        <div className={classNames("number-list")}>
          <NumberListIcon />
        </div>
        <div className={classNames("bullet")} onClick={formatBulletList}>
          <BulletPointIcon />
        </div>
        <div className={classNames("sub-list")}>
          <LeftAlignIcon />
        </div>
        <div className={classNames("sub-list-non")}>
          <RightAlignIcon />
        </div>
        <ToolBoxDivider />
      </div>
      <div className="submit-btn">
        <Button
          label="Send"
          type="score"
          icon={false}
          styles="btn-custom-style"
        />
      </div>
    </div>
  );
};

export const TextEditor = () => {
  const initialConfig = {
    namespace: "MyEditor",
    theme,
    onError,
  };

  return (
    <div className="editor-wrapper">
      <div className="editor-container">
        <LexicalComposer initialConfig={initialConfig}>
          <Toolbar />
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className="contentEditable"
                style={{ outline: "none" }}
              />
            }
            placeholder={<div className="placeholder">Enter some text...</div>}
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          <AutoFocusPlugin />
        </LexicalComposer>
      </div>
    </div>
  );
};
